import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiClock, FiX } from "react-icons/fi";
import { addDays, format, startOfWeek, subWeeks, addWeeks } from "date-fns";
import { fr } from "date-fns/locale/fr";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Badge from "../components/ui/Badge";
import Select from "../components/ui/Select";
import Input from "../components/ui/Input";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import EmptyState from "../components/ui/EmptyState";
import {
  timesheetsApi,
  Timesheet,
  TimesheetLineInput,
} from "../api/timesheets";
import toast from "react-hot-toast";

const ACTIVITY_OPTIONS = [
  { value: "bureau", label: "Bureau" },
  { value: "teletravail", label: "Télétravail" },
  { value: "deplacement", label: "Déplacement" },
  { value: "formation", label: "Formation" },
  { value: "autre", label: "Autre" },
];

const STATUS_VARIANT: Record<
  Timesheet["status"],
  "warning" | "success" | "danger" | "neutral"
> = {
  pending: "warning",
  approved: "success",
  rejected: "danger",
  cancelled: "neutral",
};

const WEEKDAY_LABELS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

interface DayEntry {
  work_date: string;
  hours: string;
  activity_type: string;
  description: string;
}

const buildWeekDays = (weekStart: Date): DayEntry[] =>
  Array.from({ length: 7 }, (_, i) => ({
    work_date: format(addDays(weekStart, i), "yyyy-MM-dd"),
    hours: "",
    activity_type: "bureau",
    description: "",
  }));

const MesTempsPage: React.FC = () => {
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [comment, setComment] = useState("");
  const [weekStart, setWeekStart] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 }),
  );
  const [days, setDays] = useState<DayEntry[]>(() =>
    buildWeekDays(startOfWeek(new Date(), { weekStartsOn: 1 })),
  );

  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);
  const weekStartIso = format(weekStart, "yyyy-MM-dd");

  const existingForWeek = useMemo(
    () =>
      timesheets.find(
        (t) => t.week_start === weekStartIso && t.status !== "cancelled",
      ),
    [timesheets, weekStartIso],
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await timesheetsApi.listMine();
      setTimesheets(res.data.data?.timesheets ?? []);
    } catch {
      toast.error("Impossible de charger vos feuilles de temps");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setDays(buildWeekDays(weekStart));
  }, [weekStart]);

  const changeWeek = (delta: number) => {
    setWeekStart((prev) => (delta < 0 ? subWeeks(prev, 1) : addWeeks(prev, 1)));
  };

  const updateDay = (index: number, patch: Partial<DayEntry>) => {
    setDays((prev) =>
      prev.map((d, i) => (i === index ? { ...d, ...patch } : d)),
    );
  };

  const weekTotal = useMemo(
    () => days.reduce((sum, d) => sum + (parseFloat(d.hours) || 0), 0),
    [days],
  );

  const handleSubmit = async () => {
    if (existingForWeek && existingForWeek.status !== "rejected") {
      toast.error("Une feuille existe déjà pour cette semaine");
      return;
    }

    const lines: TimesheetLineInput[] = days
      .filter((d) => parseFloat(d.hours) > 0)
      .map((d) => ({
        work_date: d.work_date,
        hours: parseFloat(d.hours),
        activity_type: d.activity_type,
        description: d.description.trim() || undefined,
      }));

    if (lines.length === 0) {
      toast.error("Saisissez au moins une journée avec des heures");
      return;
    }

    setSubmitting(true);
    try {
      await timesheetsApi.create({
        week_start: weekStartIso,
        comment: comment.trim() || undefined,
        lines,
      });
      toast.success("Feuille de temps envoyée au service RH");
      setComment("");
      setDays(buildWeekDays(weekStart));
      await loadData();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Échec de l'envoi";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (sheet: Timesheet) => {
    if (!window.confirm("Annuler cette feuille de temps ?")) return;
    try {
      await timesheetsApi.cancel(sheet.id);
      toast.success("Feuille annulée");
      await loadData();
    } catch {
      toast.error("Impossible d'annuler");
    }
  };

  const canSubmit = !existingForWeek || existingForWeek.status === "rejected";

  if (loading) {
    return (
      <div
        style={{ padding: "3rem", display: "flex", justifyContent: "center" }}
      >
        <LoadingSpinner size={36} />
      </div>
    );
  }

  return (
    <div style={{ padding: "2rem", maxWidth: 960, margin: "0 auto" }}>
      <div style={{ marginBottom: "1.5rem" }}>
        <h1
          style={{
            margin: "0 0 0.35rem",
            fontSize: "var(--font-size-2xl)",
            fontWeight: 700,
            color: "var(--color-primary)",
          }}
        >
          Feuilles de temps
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: "var(--font-size-sm)",
            color: "var(--color-text-secondary)",
          }}
        >
          Saisissez vos heures par autodéclaration : validation par le service
          RH
        </p>
      </div>

      <Card padding="1.25rem" style={{ marginBottom: "1.5rem" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "1rem",
            flexWrap: "wrap",
            gap: "0.75rem",
          }}
        >
          <Button
            variant="outline"
            size="sm"
            icon={<FiChevronLeft size={14} />}
            onClick={() => changeWeek(-1)}
          >
            Semaine préc.
          </Button>
          <div style={{ textAlign: "center" }}>
            <p
              style={{
                margin: 0,
                fontWeight: 600,
                fontSize: "var(--font-size-sm)",
              }}
            >
              Semaine du {format(weekStart, "d MMMM", { locale: fr })}
              {" au "}
              {format(weekEnd, "d MMMM yyyy", { locale: fr })}
            </p>
            {existingForWeek && (
              <Badge variant={STATUS_VARIANT[existingForWeek.status]} size="sm">
                {existingForWeek.status_label} : {existingForWeek.total_hours} h
              </Badge>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={() => changeWeek(1)}>
            Semaine suiv.
            <FiChevronRight size={14} style={{ marginLeft: 4 }} />
          </Button>
        </div>

        <div
          style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}
        >
          {days.map((day, index) => (
            <div
              key={day.work_date}
              style={{
                display: "grid",
                gridTemplateColumns: "56px 80px 1fr 1fr",
                gap: "0.5rem",
                alignItems: "end",
              }}
            >
              <div>
                <p
                  style={{
                    margin: 0,
                    fontSize: "var(--font-size-xs)",
                    fontWeight: 600,
                    color: "var(--color-text-secondary)",
                  }}
                >
                  {WEEKDAY_LABELS[index]}
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: "var(--font-size-xs)",
                    color: "var(--color-text-muted)",
                  }}
                >
                  {format(new Date(day.work_date), "dd/MM")}
                </p>
              </div>
              <Input
                label="Heures"
                type="number"
                min="0"
                max="24"
                step="0.25"
                value={day.hours}
                onChange={(e) => updateDay(index, { hours: e.target.value })}
                disabled={!canSubmit}
              />
              <Select
                label="Activité"
                value={day.activity_type}
                onChange={(e) =>
                  updateDay(index, { activity_type: e.target.value })
                }
                options={ACTIVITY_OPTIONS}
                disabled={!canSubmit}
              />
              <Input
                label="Description"
                value={day.description}
                onChange={(e) =>
                  updateDay(index, { description: e.target.value })
                }
                placeholder="Optionnel"
                disabled={!canSubmit}
              />
            </div>
          ))}
        </div>

        <div
          style={{
            marginTop: "1rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "0.75rem",
          }}
        >
          <p style={{ margin: 0, fontSize: "var(--font-size-sm)" }}>
            Total semaine : <strong>{weekTotal.toFixed(2)} h</strong>
          </p>
          {canSubmit ? (
            <Button
              icon={<FiClock size={16} />}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? "Envoi…" : "Envoyer la feuille"}
            </Button>
          ) : existingForWeek?.status === "pending" ? (
            <Button
              variant="outline"
              onClick={() => handleCancel(existingForWeek)}
            >
              Annuler la soumission
            </Button>
          ) : null}
        </div>

        {canSubmit && (
          <div style={{ marginTop: "0.75rem" }}>
            <Input
              label="Commentaire (optionnel)"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Précisions pour le service RH…"
            />
          </div>
        )}
      </Card>

      <h2
        style={{
          margin: "0 0 1rem",
          fontSize: "var(--font-size-base)",
          fontWeight: 600,
        }}
      >
        Historique
      </h2>
      {timesheets.length === 0 ? (
        <Card padding="2rem">
          <EmptyState
            title="Aucune feuille de temps"
            description="Vos déclarations apparaîtront ici après envoi."
          />
        </Card>
      ) : (
        <Card padding="0">
          {timesheets.map((sheet, index) => (
            <div
              key={sheet.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                padding: "1rem 1.25rem",
                borderTop:
                  index > 0 ? "1px solid var(--color-border-light)" : undefined,
              }}
            >
              <div style={{ flex: 1 }}>
                <p
                  style={{
                    margin: 0,
                    fontWeight: 500,
                    fontSize: "var(--font-size-sm)",
                  }}
                >
                  {sheet.week_label}
                </p>
                <p
                  style={{
                    margin: "0.15rem 0 0",
                    fontSize: "var(--font-size-xs)",
                    color: "var(--color-text-muted)",
                  }}
                >
                  {sheet.total_hours} h déclarées
                </p>
                {sheet.rh_comment && sheet.status === "rejected" && (
                  <p
                    style={{
                      margin: "0.25rem 0 0",
                      fontSize: "var(--font-size-xs)",
                      color: "var(--color-danger)",
                    }}
                  >
                    Motif RH : {sheet.rh_comment}
                  </p>
                )}
              </div>
              <Badge variant={STATUS_VARIANT[sheet.status]} size="sm">
                {sheet.status_label}
              </Badge>
              {sheet.status === "pending" && (
                <button
                  type="button"
                  title="Annuler"
                  onClick={() => handleCancel(sheet)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "var(--color-text-muted)",
                    cursor: "pointer",
                  }}
                >
                  <FiX size={18} />
                </button>
              )}
            </div>
          ))}
        </Card>
      )}
    </div>
  );
};

export default MesTempsPage;
